import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ReactFlow, Background, Controls, MiniMap,
  addEdge, useNodesState, useEdgesState, useReactFlow,
  Handle, Position, ReactFlowProvider,
  BaseEdge, EdgeLabelRenderer, getStraightPath,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { RefreshCw, Plus, Save, X, Check, Download, Trash2, Edit2, GitBranch } from 'lucide-react';
import {
  listAutoAttendants, syncAutoAttendants, createAutoAttendant, pollJob,
  updateAutoAttendant, deleteAutoAttendant, listCallQueues,
} from '../services/portalApi';

// ── Constants ─────────────────────────────────────────────────────────────────

const DTMF_KEYS = ['1','2','3','4','5','6','7','8','9','0','*','#'];
const KEY_TO_DTMF = { '0':'Tone0','1':'Tone1','2':'Tone2','3':'Tone3','4':'Tone4','5':'Tone5','6':'Tone6','7':'Tone7','8':'Tone8','9':'Tone9','*':'ToneStar','#':'TonePound' };

const NODE_PALETTE = [
  { type: 'greeting',        label: '🔊 Greeting',            hint: 'Play a message' },
  { type: 'menu',            label: '📋 Menu',                hint: 'DTMF key press routing' },
  { type: 'transferQueue',   label: '📞 Transfer → Queue',    hint: 'Send to call queue' },
  { type: 'transferPerson',  label: '👤 Transfer → Person',   hint: 'Send to a user' },
  { type: 'transferExternal',label: '🌐 Transfer → External', hint: 'Send to external number' },
  { type: 'disconnect',      label: '✕ End Call',             hint: 'Disconnect caller' },
  { type: 'voicemail',       label: '📧 Voicemail',           hint: 'Route to voicemail' },
];

function getDefaultData(type) {
  switch (type) {
    case 'greeting':         return { promptText: '' };
    case 'menu':             return { promptText: '', options: [] };
    case 'transferQueue':    return { targetId: '', targetName: '' };
    case 'transferPerson':   return { targetId: '', targetName: '' };
    case 'transferExternal': return { phoneNumber: '' };
    case 'voicemail':        return { targetId: '', targetName: '' };
    default:                 return {};
  }
}

// ── Shared node card style ─────────────────────────────────────────────────────

function card(color, selected) {
  const C = {
    start:           { bg: '#063b2e', border: '#10b981' },
    greeting:        { bg: '#0c1a4a', border: 'var(--info)' },
    menu:            { bg: '#2e1065', border: 'var(--purple)' },
    transferQueue:   { bg: '#431407', border: '#f97316' },
    transferPerson:  { bg: '#083344', border: 'var(--cyan)' },
    transferExternal:{ bg: '#1c1917', border: '#78716c' },
    disconnect:      { bg: 'var(--err-bg)', border: 'var(--err)' },
    voicemail:       { bg: '#2d1f00', border: '#eab308' },
  }[color] || { bg: '#1e293b', border: 'var(--muted-3)' };
  return {
    background: C.bg,
    border: `1.5px solid ${selected ? '#ffffff88' : C.border}`,
    borderRadius: 10,
    padding: '10px 14px',
    minWidth: 170,
    fontSize: 12,
    color: 'var(--text-2)',
    boxShadow: selected ? `0 0 0 2px ${C.border}` : '0 4px 12px rgba(0,0,0,0.4)',
  };
}

const label = (color) => ({
  start:           'var(--ok)', greeting:'var(--info)', menu:'var(--purple)',
  transferQueue:   '#fdba74', transferPerson:'var(--cyan)', transferExternal:'#d6d3d1',
  disconnect:      'var(--err)', voicemail:'#fde047',
}[color] || 'var(--text-2)');

// ── Custom Nodes ───────────────────────────────────────────────────────────────

function StartNode({ data, isConnectable }) {
  return (
    <div style={{ ...card('start'), textAlign: 'center', minWidth: 130, padding: '14px 18px' }}>
      <div style={{ fontSize: 22, lineHeight: 1 }}>▶</div>
      <div style={{ fontWeight: 700, color: 'var(--ok)', fontSize: 13, marginTop: 4 }}>Entry Point</div>
      {data.label && <div style={{ fontSize: 11, color: '#34d399', marginTop: 2 }}>{data.label}</div>}
      <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} />
    </div>
  );
}

function GreetingNode({ data, isConnectable, selected }) {
  return (
    <div style={card('greeting', selected)}>
      <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
      <div style={{ fontWeight: 600, color: label('greeting'), marginBottom: 6 }}>🔊 Greeting</div>
      <div style={{ fontSize: 11, color: 'var(--muted-2)', background: '#0d1b3e44', borderRadius: 4, padding: '3px 6px', maxWidth: 200, wordBreak: 'break-word' }}>
        {data.promptText || <em style={{ color: 'var(--muted-3)' }}>No text — click to edit</em>}
      </div>
      <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} />
    </div>
  );
}

function MenuNode({ data, isConnectable, selected }) {
  const opts = data.options || [];
  const total = opts.length + 1; // +1 for timeout handle
  const pct = (i) => `${Math.round((i + 1) * 100 / (total + 1))}%`;
  return (
    <div style={{ ...card('menu', selected), minWidth: 200 }}>
      <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
      <div style={{ fontWeight: 600, color: label('menu'), marginBottom: 6 }}>📋 Menu</div>
      {data.promptText && (
        <div style={{ fontSize: 11, color: 'var(--muted-2)', marginBottom: 6, maxWidth: 200, wordBreak: 'break-word' }}>{data.promptText}</div>
      )}
      {opts.length === 0 && <div style={{ fontSize: 11, color: 'var(--purple-bg)', fontStyle: 'italic' }}>No options — click to add</div>}
      {opts.map(o => (
        <div key={o.key} style={{ fontSize: 11, color: 'var(--purple)', padding: '1px 0', display: 'flex', gap: 6 }}>
          <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{o.key}</span>
          {o.label && <span style={{ color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>{o.label}</span>}
        </div>
      ))}
      {opts.map((o, i) => (
        <Handle key={o.key} type="source" position={Position.Bottom} id={`opt-${o.key}`}
          style={{ left: pct(i), background: 'var(--purple)' }} isConnectable={isConnectable} />
      ))}
      <Handle type="source" position={Position.Bottom} id="timeout"
        style={{ left: pct(opts.length), background: 'var(--muted-3)' }} isConnectable={isConnectable} />
    </div>
  );
}

function TransferQueueNode({ data, isConnectable, selected }) {
  return (
    <div style={card('transferQueue', selected)}>
      <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
      <div style={{ fontWeight: 600, color: label('transferQueue'), marginBottom: 4 }}>📞 Transfer → Queue</div>
      <div style={{ fontSize: 11, color: '#9a3412' }}>{data.targetName || data.targetId || <em style={{ color: '#451a03' }}>Not configured</em>}</div>
    </div>
  );
}

function TransferPersonNode({ data, isConnectable, selected }) {
  return (
    <div style={card('transferPerson', selected)}>
      <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
      <div style={{ fontWeight: 600, color: label('transferPerson'), marginBottom: 4 }}>👤 Transfer → Person</div>
      <div style={{ fontSize: 11, color: '#164e63' }}>{data.targetName || data.targetId || <em style={{ color: '#0c4a6e' }}>Not configured</em>}</div>
    </div>
  );
}

function TransferExternalNode({ data, isConnectable, selected }) {
  return (
    <div style={card('transferExternal', selected)}>
      <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
      <div style={{ fontWeight: 600, color: label('transferExternal'), marginBottom: 4 }}>🌐 Transfer → External</div>
      <div style={{ fontSize: 11, color: '#78716c' }}>{data.phoneNumber || <em style={{ color: '#44403c' }}>No number</em>}</div>
    </div>
  );
}

function DisconnectNode({ data: _, isConnectable, selected }) {
  return (
    <div style={{ ...card('disconnect', selected), textAlign: 'center', minWidth: 130, padding: '14px 18px' }}>
      <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
      <div style={{ fontSize: 18 }}>✕</div>
      <div style={{ fontWeight: 600, color: label('disconnect'), fontSize: 12, marginTop: 4 }}>End Call</div>
    </div>
  );
}

function VoicemailNode({ data, isConnectable, selected }) {
  return (
    <div style={card('voicemail', selected)}>
      <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
      <div style={{ fontWeight: 600, color: label('voicemail'), marginBottom: 4 }}>📧 Voicemail</div>
      <div style={{ fontSize: 11, color: '#854d0e' }}>{data.targetName || data.targetId || <em style={{ color: '#713f12' }}>Not configured</em>}</div>
    </div>
  );
}

// Labelled edge
function LabelledEdge({ id, sourceX, sourceY, targetX, targetY, label: edgeLabel, style, markerEnd }) {
  const [edgePath, labelX, labelY] = getStraightPath({ sourceX, sourceY, targetX, targetY });
  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      {edgeLabel && (
        <EdgeLabelRenderer>
          <div style={{
            position: 'absolute', transform: `translate(-50%,-50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all', fontSize: 11, fontWeight: 700, color: 'var(--text-2)',
            background: 'var(--purple-bg)', border: '1px solid var(--purple-bg)', borderRadius: 10,
            padding: '1px 7px', zIndex: 10,
          }}>
            {edgeLabel}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const NODE_TYPES = {
  start: StartNode, greeting: GreetingNode, menu: MenuNode,
  transferQueue: TransferQueueNode, transferPerson: TransferPersonNode,
  transferExternal: TransferExternalNode, disconnect: DisconnectNode,
  voicemail: VoicemailNode,
};
const EDGE_TYPES = { labelled: LabelledEdge };

// ── PowerShell script generator ────────────────────────────────────────────────

function flowToPs(aaName, nodes, edges) {
  const getEdgesFrom = (nid, hid = null) => edges.filter(e => e.source === nid && (!hid || e.sourceHandle === hid));
  const getNode = (id) => nodes.find(n => n.id === id);

  const startEdge = getEdgesFrom('start')[0];
  const afterStart = startEdge ? getNode(startEdge.target) : null;

  const lines = [
    `# Auto Attendant: ${aaName}`,
    `# Generated by Damovo Hosting Portal`,
    `# Review and adjust UPNs/IDs before running`,
    '',
  ];

  let greetingNode = null, menuNode = null;
  if (afterStart?.type === 'greeting') {
    greetingNode = afterStart;
    const ne = getEdgesFrom(greetingNode.id)[0];
    menuNode = ne ? getNode(ne.target) : null;
  } else if (afterStart?.type === 'menu') {
    menuNode = afterStart;
  }

  if (greetingNode) {
    lines.push(`$greeting = New-CsAutoAttendantPrompt -TextToSpeechPrompt '${(greetingNode.data.promptText || 'Welcome.').replace(/'/g, "''")}'`);
  }
  if (menuNode?.data?.promptText) {
    lines.push(`$menuPrompt = New-CsAutoAttendantPrompt -TextToSpeechPrompt '${menuNode.data.promptText.replace(/'/g, "''")}'`);
  }

  if (menuNode) {
    const optLines = [];
    for (const opt of (menuNode.data.options || [])) {
      const oe = getEdgesFrom(menuNode.id, `opt-${opt.key}`)[0];
      const tgt = oe ? getNode(oe.target) : null;
      const dtmf = KEY_TO_DTMF[opt.key] || `Tone${opt.key}`;
      if (!tgt || tgt.type === 'disconnect') {
        optLines.push(`    (New-CsAutoAttendantMenuOption -Action DisconnectCall -DtmfResponse ${dtmf})`);
      } else if (tgt.type === 'transferQueue') {
        optLines.push(`    (New-CsAutoAttendantMenuOption -Action TransferCallToTarget -DtmfResponse ${dtmf} -CallTarget (New-CsAutoAttendantCallableEntity -Identity '${tgt.data.targetId || 'queue-resource-account@domain.com'}' -Type ApplicationEndpoint))`);
      } else if (tgt.type === 'transferPerson') {
        optLines.push(`    (New-CsAutoAttendantMenuOption -Action TransferCallToTarget -DtmfResponse ${dtmf} -CallTarget (New-CsAutoAttendantCallableEntity -Identity '${tgt.data.targetId || 'user@domain.com'}' -Type User))`);
      } else if (tgt.type === 'transferExternal') {
        optLines.push(`    (New-CsAutoAttendantMenuOption -Action TransferCallToTarget -DtmfResponse ${dtmf} -CallTarget (New-CsAutoAttendantCallableEntity -Identity '${tgt.data.phoneNumber || '+1234567890'}' -Type ExternalPstn))`);
      } else if (tgt.type === 'voicemail') {
        optLines.push(`    (New-CsAutoAttendantMenuOption -Action TransferCallToTarget -DtmfResponse ${dtmf} -CallTarget (New-CsAutoAttendantCallableEntity -Identity '${tgt.data.targetId || 'voicemail-group@domain.com'}' -Type SharedVoicemail))`);
      }
    }
    lines.push(`$menu = New-CsAutoAttendantMenu -Name 'Main Menu'${menuNode.data.promptText ? ' -Prompts @($menuPrompt)' : ''} -MenuOptions @(`);
    optLines.forEach((l, i) => lines.push(l + (i < optLines.length - 1 ? ',' : '')));
    lines.push(')');
    const greetArg = greetingNode ? ' -Greetings @($greeting)' : '';
    lines.push(`$callFlow = New-CsAutoAttendantCallFlow -Name 'Default'${greetArg} -Menu $menu`);
  } else {
    const greetArg = greetingNode ? ' -Greetings @($greeting)' : '';
    lines.push(`$defaultMenu = New-CsAutoAttendantMenu -Name 'Default' -MenuOptions @(New-CsAutoAttendantMenuOption -Action DisconnectCall -DtmfResponse Tone0)`);
    lines.push(`$callFlow = New-CsAutoAttendantCallFlow -Name 'Default'${greetArg} -Menu $defaultMenu`);
  }

  lines.push('');
  lines.push(`New-CsAutoAttendant -Name '${aaName.replace(/'/g, "''")}' -LanguageId 'en-US' -TimeZoneId 'UTC' -DefaultCallFlow $callFlow`);
  return lines.join('\n');
}

// ── Node Properties Panel ──────────────────────────────────────────────────────

function NodeProperties({ node, onChange, onDelete, queues }) {
  if (!node) {
    return (
      <div style={{ padding: 20, color: 'var(--muted-3)', fontSize: 12, fontStyle: 'italic' }}>
        Click a node to edit its properties
      </div>
    );
  }

  const { type, data, id } = node;

  return (
    <div style={{ padding: '12px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>
          {NODE_PALETTE.find(p => p.type === type)?.label || type}
        </span>
        {id !== 'start' && (
          <button onClick={onDelete} style={{ background: 'var(--err-bg)', border: '1px solid var(--err)', color: 'var(--err)', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>
            Delete
          </button>
        )}
      </div>

      {type === 'greeting' && (
        <div>
          <label style={{ fontSize: 11, color: 'var(--muted-2)' }}>Greeting text (TTS)</label>
          <textarea
            value={data.promptText || ''}
            onChange={e => onChange({ promptText: e.target.value })}
            placeholder="Welcome to Acme Corp. Please listen carefully as our menu has changed."
            rows={4}
            style={{ width: '100%', marginTop: 4, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-2)', fontSize: 12, padding: '6px 8px', resize: 'vertical', boxSizing: 'border-box' }}
          />
        </div>
      )}

      {type === 'menu' && (
        <div>
          <label style={{ fontSize: 11, color: 'var(--muted-2)' }}>Menu prompt text (TTS)</label>
          <textarea
            value={data.promptText || ''}
            onChange={e => onChange({ promptText: e.target.value })}
            placeholder="For sales, press 1. For support, press 2."
            rows={3}
            style={{ width: '100%', marginTop: 4, marginBottom: 10, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-2)', fontSize: 12, padding: '6px 8px', resize: 'vertical', boxSizing: 'border-box' }}
          />
          <div style={{ fontSize: 11, color: 'var(--muted-2)', marginBottom: 6 }}>DTMF Options</div>
          {(data.options || []).map((opt, i) => (
            <div key={opt.key} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <div style={{ background: 'var(--purple-bg)', border: '1px solid var(--purple-bg)', borderRadius: 6, padding: '4px 8px', fontFamily: 'monospace', fontSize: 12, color: 'var(--purple)', minWidth: 28, textAlign: 'center' }}>{opt.key}</div>
              <input
                value={opt.label || ''}
                onChange={e => {
                  const opts = [...(data.options || [])];
                  opts[i] = { ...opts[i], label: e.target.value };
                  onChange({ options: opts });
                }}
                placeholder="Label (e.g. Sales)"
                style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-2)', fontSize: 12, padding: '4px 8px' }}
              />
              <button onClick={() => {
                const opts = (data.options || []).filter((_, j) => j !== i);
                onChange({ options: opts });
              }} style={{ background: 'none', border: 'none', color: 'var(--muted-3)', cursor: 'pointer', fontSize: 14 }}>✕</button>
            </div>
          ))}
          <div style={{ marginTop: 6 }}>
            <label style={{ fontSize: 11, color: 'var(--muted-2)' }}>Add key</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              {DTMF_KEYS.filter(k => !(data.options || []).find(o => o.key === k)).map(k => (
                <button key={k} onClick={() => onChange({ options: [...(data.options || []), { key: k, label: '' }] })}
                  style={{ background: 'var(--purple-bg)', border: '1px solid var(--purple-bg)', borderRadius: 6, color: 'var(--purple)', padding: '3px 8px', fontSize: 12, cursor: 'pointer', fontFamily: 'monospace' }}>
                  {k}
                </button>
              ))}
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 8, fontStyle: 'italic' }}>
            Connect each key's handle to a destination node. A "Timeout" handle is always available.
          </div>
        </div>
      )}

      {type === 'transferQueue' && (
        <div>
          <label style={{ fontSize: 11, color: 'var(--muted-2)' }}>Call Queue</label>
          {queues.length > 0 ? (
            <select
              value={data.targetId || ''}
              onChange={e => {
                const q = queues.find(q => q.ms_queue_id === e.target.value);
                onChange({ targetId: e.target.value, targetName: q?.name || e.target.value });
              }}
              style={{ width: '100%', marginTop: 4, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-2)', fontSize: 12, padding: '6px 8px' }}
            >
              <option value="">— Select queue —</option>
              {queues.map(q => <option key={q.id} value={q.ms_queue_id || q.id}>{q.name}</option>)}
            </select>
          ) : (
            <input value={data.targetId || ''} onChange={e => onChange({ targetId: e.target.value, targetName: e.target.value })}
              placeholder="Queue resource account UPN or ID"
              style={{ width: '100%', marginTop: 4, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-2)', fontSize: 12, padding: '6px 8px', boxSizing: 'border-box' }}
            />
          )}
        </div>
      )}

      {type === 'transferPerson' && (
        <div>
          <label style={{ fontSize: 11, color: 'var(--muted-2)' }}>User UPN or Object ID</label>
          <input value={data.targetId || ''} onChange={e => onChange({ targetId: e.target.value, targetName: e.target.value })}
            placeholder="user@domain.com"
            style={{ width: '100%', marginTop: 4, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-2)', fontSize: 12, padding: '6px 8px', boxSizing: 'border-box' }}
          />
        </div>
      )}

      {type === 'transferExternal' && (
        <div>
          <label style={{ fontSize: 11, color: 'var(--muted-2)' }}>Phone number (E.164)</label>
          <input value={data.phoneNumber || ''} onChange={e => onChange({ phoneNumber: e.target.value })}
            placeholder="+353 1 234 5678"
            style={{ width: '100%', marginTop: 4, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-2)', fontSize: 12, padding: '6px 8px', boxSizing: 'border-box' }}
          />
        </div>
      )}

      {type === 'voicemail' && (
        <div>
          <label style={{ fontSize: 11, color: 'var(--muted-2)' }}>Shared Voicemail group UPN</label>
          <input value={data.targetId || ''} onChange={e => onChange({ targetId: e.target.value, targetName: e.target.value })}
            placeholder="vm-group@domain.com"
            style={{ width: '100%', marginTop: 4, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-2)', fontSize: 12, padding: '6px 8px', boxSizing: 'border-box' }}
          />
        </div>
      )}

      {type === 'disconnect' && (
        <div style={{ fontSize: 12, color: 'var(--muted-3)', fontStyle: 'italic' }}>
          No configuration needed — call will be disconnected.
        </div>
      )}

      {type === 'start' && (
        <div style={{ fontSize: 12, color: 'var(--muted-3)', fontStyle: 'italic' }}>
          Entry point — cannot be deleted or moved to another position.
        </div>
      )}
    </div>
  );
}

// ── Main canvas (needs ReactFlowProvider) ──────────────────────────────────────

function FlowCanvas({ editMode, queues, nodes, edges, setNodes, setEdges, onNodesChange, onEdgesChange, selectedNode, setSelectedNode }) {
  const { screenToFlowPosition } = useReactFlow();
  const wrapperRef = useRef(null);

  const onConnect = useCallback((params) => {
    setEdges(eds => addEdge({ ...params, type: 'labelled', style: { stroke: 'var(--purple-bg)', strokeWidth: 1.5 } }, eds));
  }, [setEdges]);

  const onDragOver = useCallback((e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('application/reactflow');
    if (!type) return;
    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const id = `${type}-${Date.now()}`;
    setNodes(nds => [...nds, { id, type, position, data: getDefaultData(type) }]);
  }, [screenToFlowPosition, setNodes]);

  const onNodeClick = useCallback((_, node) => setSelectedNode(node), [setSelectedNode]);
  const onPaneClick = useCallback(() => setSelectedNode(null), [setSelectedNode]);

  const updateNodeData = useCallback((nodeId, newData) => {
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n));
    setSelectedNode(prev => prev?.id === nodeId ? { ...prev, data: { ...prev.data, ...newData } } : prev);
  }, [setNodes, setSelectedNode]);

  const deleteNode = useCallback(() => {
    if (!selectedNode || selectedNode.id === 'start') return;
    setNodes(nds => nds.filter(n => n.id !== selectedNode.id));
    setEdges(eds => eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));
    setSelectedNode(null);
  }, [selectedNode, setNodes, setEdges, setSelectedNode]);

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* Palette */}
      {editMode && (
        <div style={{ width: 170, background: 'var(--bg)', borderRight: '1px solid var(--border)', padding: 12, overflowY: 'auto', flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--muted-3)', fontWeight: 600, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Drag to add</div>
          {NODE_PALETTE.map(({ type, label: lbl, hint }) => (
            <div
              key={type}
              draggable
              onDragStart={e => e.dataTransfer.setData('application/reactflow', type)}
              title={hint}
              style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', marginBottom: 6, fontSize: 12, color: 'var(--muted)', cursor: 'grab', userSelect: 'none' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--muted-3)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              {lbl}
            </div>
          ))}
          <div style={{ fontSize: 10, color: 'var(--muted-3)', marginTop: 12, lineHeight: 1.5 }}>
            Drag nodes onto the canvas, then connect them by drawing edges between handles.
          </div>
        </div>
      )}

      {/* Canvas */}
      <div ref={wrapperRef} style={{ flex: 1, position: 'relative' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={editMode ? onNodesChange : undefined}
          onEdgesChange={editMode ? onEdgesChange : undefined}
          onConnect={editMode ? onConnect : undefined}
          onDrop={editMode ? onDrop : undefined}
          onDragOver={editMode ? onDragOver : undefined}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={NODE_TYPES}
          edgeTypes={EDGE_TYPES}
          fitView
          fitViewOptions={{ padding: 0.25 }}
          nodesDraggable={editMode}
          nodesConnectable={editMode}
          elementsSelectable
          deleteKeyCode={editMode ? 'Delete' : null}
          style={{ background: '#070b14' }}
        >
          <Background color="var(--border)" gap={20} />
          <Controls style={{ button: { background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--muted)' } }} />
          <MiniMap style={{ background: 'var(--bg)', border: '1px solid var(--border)' }} nodeColor="var(--border)" />
        </ReactFlow>
      </div>

      {/* Properties panel */}
      {selectedNode && (
        <div style={{ width: 240, background: 'var(--bg)', borderLeft: '1px solid var(--border)', overflowY: 'auto', flexShrink: 0 }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--muted-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Properties</div>
          <NodeProperties
            node={selectedNode}
            onChange={(newData) => updateNodeData(selectedNode.id, newData)}
            onDelete={deleteNode}
            queues={queues}
          />
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function AutoAttendants({ user, viewAsCustomer, customers = [] }) {
  const effectiveCid = viewAsCustomer?.id ?? (user?.isAdmin ? null : user?.customerId);

  const [aas,          setAas]          = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [syncing,      setSyncing]      = useState(false);
  const [flash,        setFlash]        = useState(null);
  const [selectedAa,   setSelectedAa]   = useState(null);
  const [editMode,     setEditMode]     = useState(false);
  const [aaName,       setAaName]       = useState('');
  const [showPs,       setShowPs]       = useState(false);
  const [psScript,     setPsScript]     = useState('');
  const [selectedNode, setSelectedNode] = useState(null);
  const [queues,       setQueues]       = useState([]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  function flashMsg(type, text) {
    setFlash({ type, text });
    setTimeout(() => setFlash(null), 4000);
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAutoAttendants(effectiveCid);
      setAas(data.autoAttendants || []);
    } catch (e) {
      flashMsg('error', e.message);
    } finally {
      setLoading(false);
    }
  }, [effectiveCid]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    listCallQueues(effectiveCid).then(d => setQueues(d.callQueues || [])).catch(() => {});
  }, [effectiveCid]);

  function selectAa(aa) {
    setSelectedAa(aa);
    setAaName(aa.name);
    setEditMode(false);
    setSelectedNode(null);
    try {
      const flow = JSON.parse(aa.flow_json || '{"nodes":[],"edges":[]}');
      setNodes(flow.nodes || []);
      setEdges(flow.edges || []);
    } catch {
      setNodes([]);
      setEdges([]);
    }
  }

  function newAa() {
    setSelectedAa({ id: null, name: '' });
    setAaName('');
    setEditMode(true);
    setSelectedNode(null);
    setNodes([{ id: 'start', type: 'start', position: { x: 280, y: 80 }, data: {}, deletable: false }]);
    setEdges([]);
  }

  async function saveFlow() {
    const name = aaName.trim();
    if (!name) { flashMsg('error', 'Name is required'); return; }
    const flow_json = JSON.stringify({ nodes, edges });
    try {
      if (selectedAa?.id) {
        await updateAutoAttendant(selectedAa.id, { name, flow_json });
        flashMsg('success', 'Saved');
        await load();
        setEditMode(false);
      } else {
        const cid = effectiveCid ?? (user?.isAdmin ? null : user?.id);
        if (!cid && user?.isAdmin) { flashMsg('error', 'Select a customer first'); return; }
        const res = await createAutoAttendant({ name, flow_json, customerId: cid });
        flashMsg('success', 'Created');
        await load();
        setSelectedAa(prev => ({ ...prev, id: res.id, name }));
        setEditMode(false);
      }
    } catch (e) {
      flashMsg('error', e.message);
    }
  }

  async function handleSync() {
    setSyncing(true);
    try {
      const { jobId } = await syncAutoAttendants(effectiveCid);
      flashMsg('success', 'Sync started — this can take a couple of minutes…');
      pollJob(jobId, job => {
        if (job.status === 'done') {
          flashMsg('success', job.message || 'Sync complete');
          load();
        } else {
          flashMsg('error', job.message || 'Sync failed');
        }
        setSyncing(false);
      });
    } catch (e) {
      flashMsg('error', e.message);
      setSyncing(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this auto attendant from the portal? (Does not affect Teams.)')) return;
    try {
      await deleteAutoAttendant(id);
      if (selectedAa?.id === id) { setSelectedAa(null); setNodes([]); setEdges([]); }
      flashMsg('success', 'Deleted');
      await load();
    } catch (e) {
      flashMsg('error', e.message);
    }
  }

  function generatePs() {
    const script = flowToPs(aaName || 'My Auto Attendant', nodes, edges);
    setPsScript(script);
    setShowPs(true);
  }

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', overflow: 'hidden' }}>
      {flash && (
        <div className={`flash flash-${flash.type}`} style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999 }}>
          {flash.type === 'success' ? <Check size={14} /> : <X size={14} />}
          {flash.text}
        </div>
      )}

      {/* PS Script modal */}
      {showPs && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000cc', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, width: '100%', maxWidth: 780, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, color: 'var(--text-2)', fontSize: 14 }}>PowerShell Script</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => navigator.clipboard.writeText(psScript)} className="btn btn-secondary" style={{ fontSize: 12 }}>Copy</button>
                <button onClick={() => setShowPs(false)} style={{ background: 'none', border: 'none', color: 'var(--muted-2)', cursor: 'pointer' }}><X size={18} /></button>
              </div>
            </div>
            <pre style={{ flex: 1, overflowY: 'auto', padding: 20, margin: 0, fontSize: 12, color: 'var(--muted)', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {psScript}
            </pre>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--muted-3)' }}>
              Review and adjust UPNs / resource account identities before running. Run in a Teams PowerShell session with appropriate permissions.
            </div>
          </div>
        </div>
      )}

      {/* Page header */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div>
          <h1 className="page-title">Auto Attendants</h1>
          <p className="page-subtitle">Design and manage Teams auto attendant call flows</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={handleSync} disabled={syncing}>
            <RefreshCw size={14} className={syncing ? 'spin' : ''} />
            {syncing ? 'Syncing…' : 'Sync from Teams'}
          </button>
          <button className="btn btn-primary" onClick={newAa}>
            <Plus size={14} /> New Auto Attendant
          </button>
        </div>
      </div>

      {/* Main layout */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', gap: 0 }}>

        {/* Left: AA list */}
        <div style={{ width: 230, background: 'var(--bg)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--muted-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
            Auto Attendants {loading ? '…' : `(${aas.length})`}
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {aas.length === 0 && !loading && (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--muted-3)', fontStyle: 'italic' }}>
                No auto attendants yet. Sync from Teams or create a new one.
              </div>
            )}
            {aas.map(aa => (
              <div
                key={aa.id}
                onClick={() => selectAa(aa)}
                style={{
                  padding: '10px 14px', borderBottom: '1px solid var(--bg)', cursor: 'pointer',
                  background: selectedAa?.id === aa.id ? 'var(--bg)' : 'transparent',
                  borderLeft: selectedAa?.id === aa.id ? '3px solid var(--purple)' : '3px solid transparent',
                }}
                onMouseEnter={e => { if (selectedAa?.id !== aa.id) e.currentTarget.style.background = 'var(--bg)'; }}
                onMouseLeave={e => { if (selectedAa?.id !== aa.id) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-2)', marginBottom: 2 }}>
                      <GitBranch size={12} style={{ color: 'var(--purple)', marginRight: 5, verticalAlign: 'middle' }} />
                      {aa.name}
                    </div>
                    {user?.isAdmin && aa.customer_name && (
                      <div style={{ fontSize: 11, color: 'var(--muted-3)' }}>{aa.customer_name}</div>
                    )}
                    {aa.ms_aa_id && (
                      <div style={{ fontSize: 10, color: 'var(--muted-3)', marginTop: 2 }}>Synced from Teams</div>
                    )}
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); handleDelete(aa.id); }}
                    style={{ background: 'none', border: 'none', color: 'var(--muted-3)', cursor: 'pointer', padding: 2, flexShrink: 0 }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--err)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--muted-3)'}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: canvas */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Canvas toolbar */}
          <div style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {selectedAa ? (
              <>
                {editMode ? (
                  <input
                    value={aaName}
                    onChange={e => setAaName(e.target.value)}
                    placeholder="Auto Attendant name"
                    style={{ background: 'var(--bg)', border: '1px solid var(--muted-3)', borderRadius: 6, color: 'var(--text-2)', fontSize: 13, padding: '4px 10px', fontWeight: 600, minWidth: 220 }}
                  />
                ) : (
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>
                    <GitBranch size={14} style={{ color: 'var(--purple)', marginRight: 6, verticalAlign: 'middle' }} />
                    {aaName}
                  </span>
                )}
                <div style={{ flex: 1 }} />
                {editMode ? (
                  <>
                    <button className="btn btn-secondary" onClick={() => { if (selectedAa.id) selectAa(selectedAa); else { setSelectedAa(null); setNodes([]); setEdges([]); } setEditMode(false); }} style={{ fontSize: 12 }}>
                      <X size={13} /> Cancel
                    </button>
                    <button className="btn btn-secondary" onClick={generatePs} style={{ fontSize: 12 }}>
                      <Download size={13} /> Export PS Script
                    </button>
                    <button className="btn btn-primary" onClick={saveFlow} style={{ fontSize: 12 }}>
                      <Save size={13} /> Save
                    </button>
                  </>
                ) : (
                  <>
                    <button className="btn btn-secondary" onClick={generatePs} style={{ fontSize: 12 }}>
                      <Download size={13} /> Export PS Script
                    </button>
                    <button className="btn btn-primary" onClick={() => setEditMode(true)} style={{ fontSize: 12 }}>
                      <Edit2 size={13} /> Edit Flow
                    </button>
                  </>
                )}
              </>
            ) : (
              <span style={{ fontSize: 13, color: 'var(--muted-3)', fontStyle: 'italic' }}>
                Select an auto attendant or create a new one
              </span>
            )}
          </div>

          {/* Canvas */}
          {selectedAa ? (
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <ReactFlowProvider>
                <FlowCanvas
                  editMode={editMode}
                  queues={queues}
                  nodes={nodes}
                  edges={edges}
                  setNodes={setNodes}
                  setEdges={setEdges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  selectedNode={selectedNode}
                  setSelectedNode={setSelectedNode}
                />
              </ReactFlowProvider>
            </div>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
              <GitBranch size={48} style={{ color: 'var(--border)' }} />
              <div style={{ color: 'var(--muted-3)', fontSize: 14 }}>Select an auto attendant to view its flow</div>
              <button className="btn btn-primary" onClick={newAa} style={{ marginTop: 4 }}>
                <Plus size={14} /> Create Auto Attendant
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
